/**
 * The navbar's behaviour on the markup the page already has: its dropdown
 * menus, and the toggle that opens and closes the sidebar.
 */
import { isHtmlElement } from '@crown/framework/dom';
import type { Signal } from '@crown/signal';
import { effect } from '../../signal/effect';

export function init(
  element: HTMLElement,
  leftSidebarOpenSignal?: Signal.State<boolean>,
  _leftSidebarMiniSignal?: Signal.State<boolean>,
): () => void {
  const dropdownTriggers = element.querySelectorAll<HTMLElement>(
    '[data-dls-dropdown-trigger]',
  );

  const toggleDropdown = (trigger: HTMLElement) => {
    const menuId = trigger.getAttribute('aria-controls');
    const menu = menuId ? document.getElementById(menuId) : null;

    if (!menu) return;

    const isExpanded = trigger.getAttribute('aria-expanded') === 'true';
    trigger.setAttribute('aria-expanded', (!isExpanded).toString());
    menu.classList.toggle('hidden', isExpanded);
  };

  const closeAllDropdowns = () => {
    dropdownTriggers.forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'false');
      const menuId = trigger.getAttribute('aria-controls');
      const menu = menuId ? document.getElementById(menuId) : null;
      if (menu) {
        menu.classList.add('hidden');
      }
    });
  };

  const handleDropdownClick = (event: MouseEvent) => {
    const trigger = event.currentTarget;
    event.stopPropagation();
    if (isHtmlElement(trigger)) toggleDropdown(trigger);
  };

  const handleDocumentClick = (event: MouseEvent) => {
    const target = event.target;
    const isDropdownClick =
      target instanceof Node && Array.from(dropdownTriggers).some((trigger) => trigger.contains(target));

    if (!isDropdownClick) {
      closeAllDropdowns();
    }
  };

  dropdownTriggers.forEach((trigger) => {
    trigger.addEventListener('click', handleDropdownClick);
  });

  document.addEventListener('click', handleDocumentClick);

  const sidebarToggle =
    element.querySelector<HTMLButtonElement>('.navbar-toggle');

  if (sidebarToggle && leftSidebarOpenSignal) {
    const handleSidebarToggle = (event: Event) => {
      event.preventDefault();
      leftSidebarOpenSignal.set(!leftSidebarOpenSignal.get());
    };

    sidebarToggle.addEventListener('click', handleSidebarToggle);

    const sidebarToggleEffectCleanup = effect(() => {
      const isOpen = leftSidebarOpenSignal.get();
      sidebarToggle.setAttribute('aria-expanded', String(isOpen));
      sidebarToggle.setAttribute(
        'aria-label',
        isOpen ? 'Close sidebar' : 'Open sidebar',
      );

      const srOnlyLabel = sidebarToggle.querySelector('.sr-only');
      if (srOnlyLabel) {
        srOnlyLabel.textContent = isOpen ? 'Close sidebar' : 'Open sidebar';
      }
    });

    return () => {
      dropdownTriggers.forEach((trigger) => {
        trigger.removeEventListener('click', handleDropdownClick);
      });
      document.removeEventListener('click', handleDocumentClick);
      sidebarToggle.removeEventListener('click', handleSidebarToggle);
      sidebarToggleEffectCleanup?.();
    };
  }

  return () => {
    dropdownTriggers.forEach((trigger) => {
      trigger.removeEventListener('click', handleDropdownClick);
    });
    document.removeEventListener('click', handleDocumentClick);
  };
}
